/**
 * DealPhotosTab — the deal's CompanyCam photos (Oct 1, 2026).
 *
 * Crews shoot in the CompanyCam app; this tab shows what's mirrored from it
 * (companycam_photos), split into job phases by capture date:
 * Pre-inspection · Reinspection · Build day · Warranty (see photoPhase()).
 * Each photo keeps CompanyCam's capture time, GPS and who took it; tapping one
 * opens it large with the full-size original a tap away.
 *
 * No project linked → shows likely CompanyCam projects to link (address
 * matches + a search), for the roles companycam_link_project() admits.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import {
  MATCH_LABEL, PHASE_META, PHASE_ORDER, dayKey, distanceLabel, fetchDealPhotos, fetchDealProjects,
  fetchLinkCandidates, fmtCaptured, linkProject, photoPhase, refreshDealPhotos, searchProjects, useCompanyCamRealtime,
  type CcPhoto, type CcProject, type PhotoPhase,
} from "../../lib/companycam";

interface DealLike {
  id: string;
  property_street: string | null;
  property_city?: string | null;
  property_lat?: number | null;
  property_lon?: number | null;
  adjuster_meeting_date?: string | null;
  build_completed_date?: string | null;
  build_scheduled_date?: string | null;
}

const LINK_ROLES = new Set(["admin", "sales_director", "director_of_operations", "roof_director",
  "roof_team_manager", "asm", "project_manager", "finance", "office"]);

export default function DealPhotosTab({ deal }: { deal: DealLike }) {
  const { role, isSuperUser } = useAuth();
  const canLink = isSuperUser || LINK_ROLES.has(String(role ?? ""));
  const [projects, setProjects] = useState<CcProject[]>([]);
  const [photos, setPhotos] = useState<CcPhoto[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [phase, setPhase] = useState<PhotoPhase | "all">("all");
  const [open, setOpen] = useState<CcPhoto | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [p, ph] = await Promise.all([fetchDealProjects(deal.id), fetchDealPhotos([deal.id])]);
      setProjects(p); setPhotos(ph); setErr(null);
    } catch (e) { setErr((e as Error).message); }
    finally { setLoading(false); }
  }, [deal.id]);
  useEffect(() => { void load(); }, [load]);
  useCompanyCamRealtime([deal.id], load);

  const withPhase = useMemo(() => photos.map((p) => ({ p, phase: photoPhase(p, deal) })), [photos, deal]);
  const counts = useMemo(() => {
    const c: Record<PhotoPhase, number> = { pre_inspection: 0, reinspection: 0, build: 0, warranty: 0 };
    for (const x of withPhase) c[x.phase]++;
    return c;
  }, [withPhase]);

  const sections = useMemo(() => {
    const phases = phase === "all" ? PHASE_ORDER : [phase];
    return phases.map((ph) => {
      const items = withPhase.filter((x) => x.phase === ph).map((x) => x.p);
      const byDay: Array<{ day: string; items: CcPhoto[] }> = [];
      for (const it of items) {
        const d = dayKey(it.captured_at);
        const last = byDay[byDay.length - 1];
        if (last && last.day === d) last.items.push(it); else byDay.push({ day: d, items: [it] });
      }
      return { phase: ph, total: items.length, byDay };
    }).filter((s) => s.total > 0 || phase !== "all");
  }, [withPhase, phase]);

  const refresh = async () => {
    setRefreshing(true); setFlash(null);
    try { const r = await refreshDealPhotos(deal.id); setFlash(`Pulled ${r.photos} photo${r.photos === 1 ? "" : "s"} from CompanyCam.`); await load(); }
    catch (e) { setFlash((e as Error).message); }
    finally { setRefreshing(false); }
  };

  if (loading) return <div className="p-6 text-sm text-slate-500">Loading photos…</div>;

  return (
    <div className="space-y-4">
      {err && <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">Couldn't load photos: {err}</div>}

      {/* Projects */}
      <div className="rounded-xl border border-slate-200 bg-white p-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="text-sm font-bold text-slate-800">📸 CompanyCam</div>
          {projects.length > 0 && (
            <button onClick={refresh} disabled={refreshing}
              className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60">
              {refreshing ? "Refreshing…" : "↻ Refresh from CompanyCam"}
            </button>
          )}
        </div>
        {flash && <div className="mt-2 text-xs text-slate-600">{flash}</div>}
        {projects.length === 0 ? (
          <LinkPanel deal={deal} canLink={canLink} onLinked={load} />
        ) : (
          <ul className="mt-2 space-y-1.5">
            {projects.map((p) => (
              <li key={p.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                <a href={p.project_url ?? `https://app.companycam.com/projects/${p.id}`} target="_blank" rel="noreferrer"
                  className="font-semibold text-sky-700 hover:underline">{p.name || p.street || `Project ${p.id}`} ↗</a>
                <span className="text-xs text-slate-500">{p.street}{p.city ? `, ${p.city}` : ""}</span>
                <span className="text-xs text-slate-400">{MATCH_LABEL[p.match_status]}</span>
                {canLink && (
                  <button className="text-xs text-slate-400 hover:text-rose-600"
                    onClick={async () => {
                      if (!window.confirm("Unlink this CompanyCam project from the deal? Its photos will no longer show here.")) return;
                      try { await linkProject(p.id, null); await load(); } catch (e) { setErr((e as Error).message); }
                    }}>unlink</button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {projects.length > 0 && (
        <>
          {/* Phase filter */}
          <div className="flex flex-wrap gap-1.5">
            <PhasePill active={phase === "all"} onClick={() => setPhase("all")} label="All" count={photos.length} tone="bg-slate-100 text-slate-800 border-slate-200" />
            {PHASE_ORDER.map((p) => (
              <PhasePill key={p} active={phase === p} onClick={() => setPhase(p)} label={PHASE_META[p].label} count={counts[p]} tone={PHASE_META[p].tone} />
            ))}
          </div>
          <div className="text-[11px] text-slate-500">
            Split by capture date — before the adjuster meeting · meeting to build day · build day + 3 weeks · after that.
          </div>

          {photos.length === 0 && (
            <div className="rounded-xl border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500">
              No photos in CompanyCam for this job yet. Photos taken in the CompanyCam app show up here within seconds.
            </div>
          )}

          {sections.map((s) => (
            <section key={s.phase} className="space-y-2">
              <div className="flex items-center gap-2">
                <span className={`rounded-full border px-2 py-0.5 text-xs font-bold ${PHASE_META[s.phase].tone}`}>{PHASE_META[s.phase].label}</span>
                <span className="text-xs text-slate-500">{s.total} photo{s.total === 1 ? "" : "s"}</span>
              </div>
              {s.total === 0 && <div className="text-xs text-slate-400">None.</div>}
              {s.byDay.map((g) => (
                <div key={g.day}>
                  <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    {g.day === "unknown" ? "No capture date" : new Date(`${g.day}T12:00:00`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" })}
                    <span className="ml-1 font-normal normal-case text-slate-400">· {g.items.length}</span>
                  </div>
                  <div className="grid grid-cols-4 gap-1.5 sm:grid-cols-6 lg:grid-cols-8">
                    {g.items.map((ph) => {
                      const dist = distanceLabel(ph, deal.property_lat, deal.property_lon);
                      return (
                        <button key={ph.id} onClick={() => setOpen(ph)} className="group relative aspect-square overflow-hidden rounded-lg bg-slate-100">
                          {ph.uri_thumb && <img src={ph.uri_thumb} alt="" loading="lazy" className="h-full w-full object-cover group-hover:opacity-90" />}
                          {dist?.far && <span className="absolute left-1 top-1 rounded bg-rose-600/90 px-1 text-[9px] font-bold text-white">{dist.text}</span>}
                          {ph.tags.length > 0 && <span className="absolute bottom-1 left-1 max-w-[90%] truncate rounded bg-black/60 px-1 text-[9px] text-white">{ph.tags[0]}</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </section>
          ))}
        </>
      )}

      {open && <PhotoViewer photo={open} deal={deal} onClose={() => setOpen(null)} />}
    </div>
  );
}

function PhasePill({ active, onClick, label, count, tone }: { active: boolean; onClick: () => void; label: string; count: number; tone: string }) {
  return (
    <button onClick={onClick}
      className={`rounded-full border px-3 py-1 text-xs font-semibold ${active ? `${tone} ring-2 ring-offset-1 ring-slate-400` : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}>
      {label} <span className="opacity-70">{count}</span>
    </button>
  );
}

export function PhotoViewer({ photo, deal, onClose }: { photo: CcPhoto; deal: Pick<DealLike, "property_lat" | "property_lon"> & PhaseDatesLike; onClose: () => void }) {
  useEffect(() => {
    const k = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [onClose]);
  const dist = distanceLabel(photo, deal.property_lat, deal.property_lon);
  const phase = photoPhase(photo, deal);
  return (
    <div role="dialog" aria-modal="true" className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 p-3" onClick={onClose}>
      <div className="max-h-full w-full max-w-4xl overflow-auto rounded-xl bg-white" onClick={(e) => e.stopPropagation()}>
        <img src={photo.uri_original ?? photo.uri_web ?? photo.uri_thumb ?? ""} alt="" className="max-h-[70vh] w-full bg-black object-contain" />
        <div className="space-y-1.5 p-3 text-sm">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`rounded-full border px-2 py-0.5 text-xs font-bold ${PHASE_META[phase].tone}`}>{PHASE_META[phase].label}</span>
            <span className="font-semibold text-slate-800">Taken {fmtCaptured(photo.captured_at)}</span>
            {photo.creator_name && <span className="text-slate-500">by {photo.creator_name}</span>}
            {dist && <span className={`text-xs font-semibold ${dist.far ? "text-rose-600" : "text-emerald-700"}`}>📍 {dist.text}</span>}
            {photo.lat != null && photo.lon != null && (
              <a className="text-xs text-sky-700 hover:underline" target="_blank" rel="noreferrer"
                href={`https://www.google.com/maps?q=${photo.lat},${photo.lon}`}>{photo.lat.toFixed(5)}, {photo.lon.toFixed(5)}</a>
            )}
          </div>
          {photo.description && <div className="text-slate-700">{photo.description}</div>}
          {photo.tags.length > 0 && (
            <div className="flex flex-wrap gap-1">{photo.tags.map((t) => <span key={t} className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-700">{t}</span>)}</div>
          )}
          <div className="flex flex-wrap gap-3 pt-1 text-xs">
            {photo.uri_original && <a href={photo.uri_original} target="_blank" rel="noreferrer" className="font-semibold text-sky-700 hover:underline">Full-size original ↗</a>}
            {photo.photo_url && <a href={photo.photo_url} target="_blank" rel="noreferrer" className="font-semibold text-sky-700 hover:underline">Open in CompanyCam ↗</a>}
            <button onClick={onClose} className="ml-auto rounded-lg border border-slate-300 px-3 py-1 font-semibold text-slate-700">Close</button>
          </div>
        </div>
      </div>
    </div>
  );
}
type PhaseDatesLike = Pick<DealLike, "adjuster_meeting_date" | "build_completed_date" | "build_scheduled_date">;

function LinkPanel({ deal, canLink, onLinked }: { deal: DealLike; canLink: boolean; onLinked: () => void }) {
  const [cands, setCands] = useState<CcProject[] | null>(null);
  const [q, setQ] = useState("");
  const [results, setResults] = useState<CcProject[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    fetchLinkCandidates(deal.id, deal.property_street).then(setCands).catch((e) => setErr((e as Error).message));
  }, [deal.id, deal.property_street]);
  useEffect(() => {
    const t = setTimeout(() => { if (q.trim().length >= 3) searchProjects(q).then(setResults).catch(() => setResults([])); else setResults([]); }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const link = async (p: CcProject) => {
    if (p.deal_id && !window.confirm(`This project is linked to another deal. Move it to this one?`)) return;
    setBusy(p.id); setErr(null);
    try { await linkProject(p.id, deal.id); onLinked(); }
    catch (e) { setErr((e as Error).message); }
    finally { setBusy(null); }
  };

  const row = (p: CcProject) => (
    <li key={p.id} className="flex flex-wrap items-center gap-2 text-sm">
      <a href={p.project_url ?? "#"} target="_blank" rel="noreferrer" className="font-semibold text-sky-700 hover:underline">{p.name || `Project ${p.id}`} ↗</a>
      <span className="text-xs text-slate-500">{p.street}{p.city ? `, ${p.city}` : ""}</span>
      {p.photo_count != null && <span className="text-xs text-slate-400">{p.photo_count} photos</span>}
      {p.deal_id && <span className="text-[10px] font-semibold text-amber-700">linked elsewhere</span>}
      {canLink && (
        <button disabled={busy === p.id} onClick={() => link(p)}
          className="ml-auto rounded-lg bg-sky-600 px-2.5 py-1 text-xs font-bold text-white disabled:opacity-60">{busy === p.id ? "Linking…" : "Link to this deal"}</button>
      )}
    </li>
  );

  return (
    <div className="mt-2 space-y-2">
      <div className="text-sm text-slate-600">No CompanyCam project is linked to this deal yet.</div>
      {err && <div className="text-xs text-rose-600">{err}</div>}
      {cands && cands.length > 0 && (
        <div>
          <div className="mb-1 text-[11px] font-bold uppercase tracking-wide text-slate-500">Likely matches</div>
          <ul className="space-y-1.5">{cands.map(row)}</ul>
        </div>
      )}
      {canLink ? (
        <div>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search CompanyCam projects by street or name…"
            className="w-full rounded-lg border border-slate-300 px-3 py-1.5 text-sm" />
          {results.length > 0 && <ul className="mt-2 space-y-1.5">{results.map(row)}</ul>}
        </div>
      ) : (
        <div className="text-xs text-slate-400">Ask Robert or the office to link the right CompanyCam project.</div>
      )}
    </div>
  );
}
